import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

export type AdminPayment = {
  id: number;
  tenant_id: number | null;
  tenant_name?: string | null;
  tenant_slug?: string | null;
  plan_id: number | null;
  plan_name?: string | null;
  payment_gateway_name?: string | null;
  purpose?: string | null;
  amount: string;
  currency: string;
  currency_symbol: string;
  method?: string | null;
  reference?: string | null;
  status: string;
  paid_at?: string | null;
  notes?: string | null;
  created_at?: string | null;
};

export async function fetchPaymentsList(params?: {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
  currency?: string;
}): Promise<
  | {
      ok: true;
      data: AdminPayment[];
      current_page: number;
      last_page: number;
      total: number;
    }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const qs = new URLSearchParams();
  qs.set('page', String(params?.page ?? 1));
  qs.set('per_page', String(params?.per_page ?? 20));
  if (params?.status) qs.set('status', params.status);
  if (params?.search) qs.set('search', params.search);
  if (params?.currency) qs.set('currency', params.currency);

  const res = await apiFetch(`/platform/payments?${qs.toString()}`);
  const out = await parseJsonResponse<AdminPayment[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  const meta: PaginationMeta | undefined = out.meta;
  const data = Array.isArray(out.data) ? out.data : [];
  return {
    ok: true,
    data,
    current_page: meta?.current_page ?? 1,
    last_page: meta?.last_page ?? 1,
    total: meta?.total ?? data.length,
  };
}

export type UpdatePaymentPayload = {
  amount?: number;
  status?: 'paid' | 'pending' | 'cancelled' | 'refunded' | 'failed';
  reference?: string | null;
  notes?: string | null;
  method?: string | null;
};

export async function updatePayment(
  id: number,
  payload: UpdatePaymentPayload,
): Promise<
  | { ok: true; data: AdminPayment }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/payments/${encodeURIComponent(String(id))}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<AdminPayment>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, data: out.data };
}

export async function deletePayment(
  id: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/payments/${encodeURIComponent(String(id))}`, {
    method: 'DELETE',
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}
